<template>
  <content-container :table-row-count="tableData.length">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-input v-model="params.name" placeholder="自由职业者名称" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.idNo" placeholder="身份证号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-input v-model="params.phone" placeholder="通讯手机号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-select
          v-model="params.signUpType"
          filterable
          placeholder="报名方式"
          clearable
          @keyup.enter="doSearch"
        >
          <el-option :label="registrationMethodLabel[ERegistrationMethod.MINI]" :value="ERegistrationMethod.MINI" />
          <el-option :label="registrationMethodLabel[ERegistrationMethod.ASSIGN]" :value="ERegistrationMethod.ASSIGN" />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.authStatus"
          filterable
          placeholder="实名状态"
          clearable
          @keyup.enter="doSearch"
        >
          <el-option :label="authStatusLabel[EAuthStatus.Yes]" :value="EAuthStatus.Yes" />
          <el-option :label="authStatusLabel[EAuthStatus.No]" :value="EAuthStatus.No" />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.hnTaxSourceStatus"
          filterable
          placeholder="海南关系绑定"
          clearable
          @keyup.enter="doSearch"
        >
          <el-option :label="freelancerTaxSourceLocationLabel[ETaxSourceLocation.YES]" :value="ETaxSourceLocation.YES" />
          <el-option :label="freelancerTaxSourceLocationLabel[ETaxSourceLocation.NO]" :value="ETaxSourceLocation.NO" />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.hnTaxStatus"
          filterable
          placeholder="税务登记"
          clearable
          @keyup.enter="doSearch"
        >
          <el-option :label="freelancerTaxSourceRegisterLabel[ETaxSourceRegister.YES]" :value="ETaxSourceRegister.YES" />
          <el-option :label="freelancerTaxSourceRegisterLabel[ETaxSourceRegister.NO]" :value="ETaxSourceRegister.NO" />
        </el-select>
      </search-col>

      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>

    <!-- 非搜索区域的额外按钮 -->
    <div>
      <el-button v-if="from=='saas'||from=='company'" v-permission="['match:customer:duty:import']" type="primary" class="mb-[20px]" @click="goImport">
        导入自由职业者
      </el-button>
      <el-button v-if="from=='saas'||from=='company'" v-permission="['match:customer:sms:send']" type="primary" class="mb-[20px]" :disabled="selectFreelancers.length<1" @click="handleSendMess">
        发送短信
      </el-button>
    </div>

    <!-- 表格数据 -->
    <el-table ref="registrationTable" v-loading="loading" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData" @selection-change="handleSelectionChange">
      <el-table-column align="center" type="selection" :min-width="55" />
      <el-table-column align="left" prop="name" label="自由职业者名称" :min-width="120" />
      <el-table-column align="left" prop="idNo" label="身份证号" :min-width="120" show-overflow-tooltip />
      <el-table-column align="left" prop="phone" label="通讯手机号" :min-width="120" />
      <el-table-column align="left" prop="signUpType" label="报名方式" :min-width="120">
        <template #default="scope">
          {{ registrationMethodLabel[scope.row.signUpType] }}
        </template>
      </el-table-column>
      <el-table-column align="center" prop="authStatus" label="实名状态" :min-width="120">
        <template #default="scope">
          <custom-tag :label="authStatusLabel[scope.row.authStatus]" :type="authStatusStyle[scope.row.authStatus]" />
        </template>
      </el-table-column>
      <el-table-column v-if="showHnTaxSourceStatusCol" align="center" prop="hnTaxSourceStatus" label="海南关系绑定" :min-width="120">
        <template #default="scope">
          <custom-tag :label="freelancerTaxSourceLocationLabel[scope.row.hnTaxSourceStatus]" :type="freelancerTaxSourceLocationStyle[scope.row.hnTaxSourceStatus]" />
        </template>
      </el-table-column>
      <el-table-column v-if="showHnTaxSourceStatusCol" align="center" prop="hnTaxStatus" label="税务登记" :min-width="120">
        <template #default="scope">
          <custom-tag :label="freelancerTaxSourceRegisterLabel[scope.row.hnTaxStatus]" :type="freelancerTaxSourceRegisterStyle[scope.row.hnTaxStatus]" />
        </template>
      </el-table-column>

      <el-table-column
        v-if="(from=='saas'||from=='company')&&buttons.length > 0"
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
      >
        <template #default="scope">
          <table-row-buttons :buttons="buttons" :row="scope.row" />
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>
  </content-container>
</template>

<script setup lang="ts">
import { computed, PropType, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { SvgIcon, TableRowButtons, CustomTag } from '@vue3/components';
import {
  ContentContainer,
  SearchContainer,
  SearchCol,
  useTblPermissionButtons,
  sendSmsFreelancer,
  queryTaxSourceList,
  useUserStore,
  queryFreelanceSignUpPage,
} from '../../../..';
import { IDutySignUpUser } from '../../../../types/crowd-source/task';
import { useRoute, useRouter } from 'vue-router';
import {
  registrationMethodLabel,
  ERegistrationMethod,
  authStatusLabel,
  authStatusStyle,
  EAuthStatus,
  freelancerTaxSourceLocationLabel,
  ETaxSourceLocation,
  freelancerTaxSourceLocationStyle,
  EFreelancerSmsType,
  freelancerTaxSourceRegisterLabel,
  freelancerTaxSourceRegisterStyle,
  ETaxSourceRegister,
} from '@core/main/types';

defineOptions({ name: 'BrokerLaborRegistrationPersonnel' });

const props = defineProps({
  from: {
    type: String as PropType<'saas' | 'company' | 'agent' | 'server'>,
    default: 'saas',
  },
});

const route = useRoute();
const router = useRouter();
const loading = ref(false);
const defaultPageSize = 12;
const params = reactive<any>({
  page: 1,
  size: defaultPageSize,
});
const total = ref(0);
const tableData = ref<IDutySignUpUser[]>([]);
const registrationTable = ref();
const selectFreelancers = ref([]);
const showHnTaxSourceStatusCol = ref(false);

const { state: userState } = useUserStore();
const companyId = computed(() => userState.identity.companyId || Number(route.query.companyId));

const currentRow = ref<IDutySignUpUser>({} as IDutySignUpUser);

const { buttons } = useTblPermissionButtons([
  {
    name: '发送短信',
    code: 'match:customer:sms:send',
    action: handleSendMess,
  },
]);

async function goImport() {
  router.push({
    name: 'BrokerLaborPersonnelImport',
    query: { dutyId: route.query.taskId },
  });
}

async function handleSendMess(row?: any) {
  if (row) {
    currentRow.value = row;
  } else {
    currentRow.value = {} as IDutySignUpUser;
  }
  loading.value = true;
  try {
    if (currentRow.value.idNo) {
      await sendSmsFreelancer({
        idNos: [currentRow.value.idNo],
        companyId: companyId.value,
        sendType: EFreelancerSmsType.HN_AUTH,
        dutyId: Number(route.query.taskId),
      });
    } else {
      const details = registrationTable.value.getSelectionRows();

      selectFreelancers.value = details.map((v) => v.idNo);
      await sendSmsFreelancer({
        idNos: selectFreelancers.value,
        companyId: companyId.value,
        sendType: EFreelancerSmsType.HN_AUTH,
        dutyId: Number(route.query.taskId),
      });
    }

    ElMessage.success('发送成功');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  loading.value = false;
}

function handleSelectionChange(val: any) {
  selectFreelancers.value = val.map((v) => v.idNo);
}

function resetForm() {
  for (const key in params) {
    if (!['page', 'size'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

function onEnter(e) {
  if (e.keyCode === 13) doSearch();
}

async function fetchTaxSource() {
  if (props.from == 'company') {
    try {
      const data = await queryTaxSourceList(companyId.value);

      if (data.length) {
        showHnTaxSourceStatusCol.value = true;
      }
    } catch (error) {
      if ((error as Error).message) ElMessage.error((error as Error).message);
    }
  } else if (props.from == 'saas') {
    showHnTaxSourceStatusCol.value = true;
  } else if (props.from == 'agent') {
    showHnTaxSourceStatusCol.value = true;
  }
}

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all } = await queryFreelanceSignUpPage({
      ...params,
      id: Number(route.query.taskId),
    });

    tableData.value = data;
    total.value = all;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

async function init() {
  await fetchData();
  await fetchTaxSource();
}

init();
</script>
